<template>
  <div>
    <p class="mt-4">
      {{ $t('processes.sidebar.main_features') }}
    </p>
    <table
      v-if="record && record.ref"
      class="table table-striped table-sm"
    >
      <tbody>
        <tr v-if="case_ref">
          <th scope="row">
            {{ $t('processes.sidebar.case_ref') }}
          </th>
          <td>{{ case_ref }}</td>
        </tr>
        <tr v-if="stProjectId">
          <th scope="row">
            {{ $t('processes.sidebar.stProjectId') }}
          </th>
          <td>{{ stProjectId }}</td>
        </tr>
        <tr v-if="stSiteId">
          <th scope="row">
            {{ $t('processes.sidebar.stSiteId') }}
          </th>
          <td>{{ stSiteId }}</td>
        </tr>
        <tr v-if="stAssetId">
          <th scope="row">
            {{ $t('processes.sidebar.stAssetId') }}
          </th>
          <td>{{ stAssetId }}</td>
        </tr>
        <tr v-if="sibelgaProjectId">
          <th scope="row">
            {{ $t('processes.sidebar.sibelgaProjectId') }}
          </th>
          <td>{{ sibelgaProjectId }}</td>
        </tr>
        <tr v-if="evtoolsLocationId">
          <th scope="row">
            {{ $t('processes.sidebar.evtoolsLocationId') }}
          </th>
          <td>{{ evtoolsLocationId }}</td>
        </tr>
        <tr v-if="realisationLocationId">
          <th scope="row">
            {{ $t('processes.sidebar.location_id') }}
          </th>
          <td class="d-flex align-items-center">
            <i class="fa fa-link mr-1 router-link" />
            <router-link :to="{ name: 'LocationList', params: { locationId: realisationLocationId } }">
              {{ realisationLocationId }}
            </router-link>
          </td>
        </tr>
        <tr v-if="record.ref && chargingPointStatus">
          <th scope="row">
            {{ $t('processes.sidebar.chargingpoint_status') }}
          </th>
          <td>
            <img
              :src="statusImage"
              width="20"
            >
            {{ $t(`status_labels.${charginpointStatusLabel}`) }}
          </td>
        </tr>
        <tr>
          <th scope="row">
            {{ $t('processes.sidebar.address') }}
          </th>
          <td v-if="address !== '-'">
            {{ address }}
            <br>
            <a
              target="_blank"
              :href="`https://www.google.com/maps/search/?api=1&amp;query=${encodeURIComponent(address)}`"
            >
              {{ $t('processes.sidebar.open_in_google_maps') }}
              <i class="icon fa-fw fal fa-arrow-up-right-from-square" />
            </a>
          </td>
          <td v-else>
            {{ address }}
          </td>
        </tr>
        <tr v-if="StatusLabel">
          <th scope="row">
            {{ $t('processes.sidebar.process_status') }}
          </th>
          <td>{{ $t(StatusLabel) || $t('processes.headers.unknown') }}</td>
        </tr>
        <!-- <tr v-if="StatusDuration && ! missingDurationInformation"> -->
        <tr>
          <th scope="row">
            {{ $t('processes.sidebar.date_last_step') }}
          </th>
          <td :title="StatusDuration">
            {{ dateOfLastStep }}
          </td>
        </tr>
        <tr
          v-for="stakeholder in stakeholders"
          :key="stakeholder.label"
        >
          <th scope="row">
            {{ stakeholder.label }}
          </th>
          <td>{{ stakeholder.value }}</td>
        </tr>
        <tr>
          <th scope="row">
            {{ $t('processes.sidebar.process_type') }}
          </th>
          <td>
            {{ $t(`processes.sidebar.process_types.${processType}`) }}
          </td>
        </tr>
        <tr v-if="category">
          <th scope="row">
            {{ $t('processes.sidebar.process_category') }}
          </th>
          <td>
            {{ $t(`processes.sidebar.process_categories.${category}`) }}
          </td>
        </tr>
        <tr v-if="showTags">
          <th scope="row">
            {{ $t('processes.sidebar.tags') }}
          </th>
          <td>
            <span v-if="currentTags.length === 0">{{ $t('processes.sidebar.no_tags') }}</span>
            <b-badge
              v-for="(tag, index) in currentTags"
              :key="`tag_${index}`"
              class="mr-1"
              variant="secondary"
            >
              <div :title="labelByUuid({ uuid: tag })">
                {{ labelByUuid({ uuid: tag }) }}
              </div>
            </b-badge>
          </td>
        </tr>
      </tbody>
    </table>
    <b-alert
      v-else
      show
      variant="info"
    >
      {{ $t('processes.sidebar.save_location_proposal') }}
    </b-alert>
    <div v-if="record.ref">
      <slot />
      <div v-if="case_ref && isNotCompleted">
        <RealisationStatusPanel
          :key="componentKey"
          class="mt-4 mb-4"
          :uuid="uuid"
          @update-component="componentKey++"
        />
        <RealisationDelete
          class="my-4"
          :uuids="[uuid]"
        />
      </div>
    </div>
  </div>
</template>

<script>
import RealisationStatusPanel from '@/components/realisation/RealisationStatusPanel.vue';
import RealisationDelete from '@/components/realisation/RealisationDelete.vue';
//import recordList from '@/helpers/recordList'
import { mapGetters } from 'vuex';

import { formatPostalCode } from '@/helpers/string';
import { msDurationToString, msToDate } from '@/helpers/date';
import { image } from '@/helpers/assets';

export default {
  name: 'ProcessSidebarMainTab',
  components: {
    RealisationStatusPanel,
    RealisationDelete
  },
  props: {
    uuid: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      componentKey: 0,
      image,
      record: {},
      docs: {}
    };
  },
  computed: {
    ...mapGetters('realisations', ['getSelectedRecord', 'currentStep']),
    ...mapGetters('tenant', [
      'isCurrentTenant',
      'getEnabledTags',
      'getLabelsByTagUuid',
      'getSelectOptionLabelByValue',
      'getEnabledContractors'
    ]),
    ...mapGetters('user', ['hasRole', 'canAccessAssetManagement']),
    ...mapGetters('chargingpoints', ['getChargingPointByUUID']),
    ...mapGetters('processes', ['getSelectedProcess', 'getCurrentProcessType']),
    ...mapGetters('realisation', ['getActiveRecordValues']),
    ...mapGetters('tenant', ['getSteps', 'getProcessFieldLabels']),

    isNewProcess() {
      return this.$route.name.startsWith('new-');
    },
    case_ref() {
      return !this.isNewProcess ? this.record?.case_ref : '-';
    },
    sibelgaProjectId() {
      return this.record.CurrentLocation.project?.sibelgaProjectId;
    },
    stProjectId() {
      return this.record.CurrentLocation.project?.stProjectId;
    },
    stSiteId() {
      return this.record.CurrentLocation.project?.stSiteId;
    },
    stAssetId() {
      return this.record.CurrentLocation.project?.stAssetId;
    },
    evtoolsLocationId() {
      return this.record.CurrentLocation.location.chargingStation?.evtoolsLocationId;
    },

    isNotCompleted() {
      return !this.record.status.completed && !this.record.status.locked;
    },
    realisationLocationId() {
      return this.canAccessAssetManagement && this.record.locationId?.full;
    },
    isCompleted() {
      return this.record.status.completed;
    },
    category () {
      return this.record.category
    },
    chargingPointStatus() {
      return (
        this.location.location.chargingStation?.chargingpointStatus ||
        this.chargingPoint?.data?.properties?.status ||
        ''
      ).trim();
    },
    statusImage() {
      return image({ name: `chargingpoint-${this.chargingPointStatus}` });
    },
    charginpointStatusLabel() {
      return this.chargingPointStatus || this.$t('status_labels.unknown');
    },
    location() {
      return this.record.CurrentLocation || {};
    },

    contractor() {
      return this.getStakeholder({ category: 'contractor' });
    },

    hasParkingSpots() {
      return this.record.CurrentLocation.Docs?.ParkingSpots?.length;
    },
    StatusLabel() {
      const currentStep = this.record.processStatus.currentStep;
      const statusLabel = this.getSteps?.find(s => s.step === currentStep)?.label[this.$i18n.locale];
      return this.isCompleted ? this.$i18n.t('processes.statusFilters.completed') : statusLabel;
    },
    StatusDuration() {
      try {
        let duration = this.record.getCurrentStepDuraction();
        return msDurationToString({ ms: Date.now() - duration.start });
      } catch (e) {
        return '';
      }
    },
    missingDurationInformation() {
      let duration = this.record.getCurrentStepDuraction();
      return duration.start === 0 || (duration.finished && duration.end === 0);
    },
    dateOfLastStep() {
      try {
        const duration = this.record.getCurrentStepDuraction();
        const lastStep = this.record.flow.state.filter(step => step.started_at).at(-1)?.started_at;
        const start = duration.start || lastStep;
        //console.log('start', start, duration, lastStep, this.record.flow.state)
        return msToDate({ ms: start });
      } catch (e) {
        return '-';
      }
    },
    chargingPoint() {
      const uuid = this.location.location?.chargingStation?.chargingpointUuid;
      return this.getChargingPointByUUID({ uuid });
    },
    address() {
      const location = this.location.location.address;

      if (!location?.streetAndNumber) return '-';
      return `${location.streetAndNumber || ''}, ${formatPostalCode(location.postalCode || '')} ${location.city || ''}`;
    },
    processType() {
      return this.record.processType.value;
    },

    /**************************************************************************
     * TAGS
     */
    currentTags() {
      return this.record.getTags();
    },

    showTags() {
      return this.getEnabledTags.length || (this.record && this.record.hasTags());
    },

    stakeholders () {
      const all = this.record.stakeholders || []

      if (this.processType === 'takeover') {
        return all.flatMap(s => {
          const labelKey = s.category === 'cpo' && s.partyRole
            ? `cpo_${s.partyRole}`
            : s.category === 'gridoperator' ? 'dsoInCharge' : s.category
          return [{ value: s.name, label: this.setFieldLabel(labelKey) }]
        }).filter(s => s.value)
      }

      const fields = ['municipality', 'gridoperator', 'cpo', 'contractor']
      return fields.map(field => ({
        value: all.find(s => s.category === field)?.name,
        label: this.setFieldLabel(field === 'gridoperator' ? 'dsoInCharge' : field)
      })).filter(s => s.value)
    }
  },
  watch: {
    getSelectedProcess: {
      deep: true,
      immediate: true,
      handler(record) {
        if (!record) return;
        this.record = record;
        this.docs = this.record.Docs;
        // Refresh status component //
        this.componentKey++;
      }
    }
  },

  methods: {
    getView(viewType) {
      return this.docs[viewType] || [];
    },
    labelByUuid({ uuid }) {
      return this.getLabelsByTagUuid[uuid] || 'Onbekende tag';
    },
    setFieldLabel(field) {
      const label = this.getProcessFieldLabels?.[field]?.[this.$i18n.locale];
      if (label) return label
      const stakeholderKey = `processes.stakeholders.${field}`
      const stakeholderLabel = this.$i18n.t(stakeholderKey)
      if (stakeholderLabel !== stakeholderKey) return stakeholderLabel
      return this.$i18n.t(`recordList.${field}`)
    }
  }
};
</script>

<style lang="scss" scoped>
.startdocList {
  display: flex;
  align-items: center;

  > h5 {
    margin-right: 0.5rem;
    margin-bottom: 0;
  }
}

.Views {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  > div {
    width: 48%;
  }
}

tbody th {
  width: 52%;
}

.router-link {
  font-size: 0.8em;
  position: relative;
  top: 1px;
}
</style>
